// src/pages/SmartPoint/CustomerWelfareManage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { customerNoOf } from '../../components/CustomerReportShell';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { formatDateTimeIST } from '../../utils/dateFormat';

// Cash ledger key 'CASH-<id>' is never shown
const vpaShown = (v) => (!v || v === 'N/A' || /^CASH-/i.test(String(v)) ? '-' : v);
const billShown = (v) => (v === null || v === undefined || isNaN(Number(v)) ? '-' : Number(v).toFixed(2));
const modeShown = (r) => (r && (r.modeOfPay === 'UPI' || r.modeOfPay === 'CASH') ? r.modeOfPay : '-');

const formatAmount4Decimals = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0.0000';
  return Number(val).toFixed(4);
};

const CustomerWelfareManage = () => {
  const { can } = usePermissions();
  const showSensitive = can('smartPoint.welfare.viewSensitive'); // mobile / VPA are hidden without it
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Search customer state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedCustomer, setSearchedCustomer] = useState(null);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);

  // Form states for Add/Deduct
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('Credit'); // 'Credit' | 'Debit'
  const [remark, setRemark] = useState('');

  // History state
  const [history, setHistory] = useState([]);
  const [tableSearch, setTableSearch] = useState('');

  // Notification states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Confirmation dialog
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchHistory = useCallback(async (search = '') => {
    setHistoryLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/customerWelfare/history`, { search });
      if (response.data?.status === 'SUCCESS') {
        setHistory(response.data.history || []);
      }
    } catch (error) {
      console.error('Error fetching Welfare Fund history:', error);
      showSnackbar('Error fetching Welfare Fund transaction history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Customer search handler
  const handleSearchCustomer = async () => {
    if (!searchQuery.trim()) {
      showSnackbar('Please enter a Mobile Number, VPA ID, or Customer No', 'warning');
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/customerWelfare/searchCustomer`, { query: searchQuery.trim() });
      if (response.data?.status === 'SUCCESS' && response.data.customer) {
        setSearchedCustomer(response.data.customer);
        showSnackbar(`Customer found: ${response.data.customer.customerName}`);
        fetchHistory(searchQuery.trim());
      } else {
        setSearchedCustomer(null);
        showSnackbar('Customer not found', 'error');
      }
    } catch (error) {
      setSearchedCustomer(null);
      const msg = error.response?.data?.message || 'Error searching customer';
      showSnackbar(msg, 'error');
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Open confirmation modal
  const handleFormSubmit = () => {
    if (!can('smartPoint.welfare.adjust')) return;
    if (!searchedCustomer) {
      showSnackbar('Please search and select a customer first', 'warning');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      showSnackbar('Please enter a valid amount greater than 0', 'warning');
      return;
    }

    if (type === 'Debit' && numAmount > searchedCustomer.availableWelfareFund) {
      showSnackbar(`Insufficient Welfare Fund balance (₹${formatAmount4Decimals(searchedCustomer.availableWelfareFund)}) for Debit of ₹${formatAmount4Decimals(numAmount)}`, 'error');
      return;
    }

    setConfirmModalOpen(true);
  };

  // Execute Add / Deduct Welfare Fund
  const handleConfirmTransaction = async () => {
    if (!can('smartPoint.welfare.adjust')) return;
    setConfirmModalOpen(false);
    setLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      const payload = {
        customerId: searchedCustomer._id,
        amount: parseFloat(amount),
        type,
        remarks: remark || `${type} Welfare Fund by Admin`
      };

      const response = await axios.post(`${API_BASE_URL}/customerWelfare/addDeduct`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data?.status === 'SUCCESS') {
        showSnackbar(`Welfare Fund ${type === 'Credit' ? 'credited' : 'debited'} successfully!`);
        
        // Update local available balance
        setSearchedCustomer(prev => prev ? { ...prev, availableWelfareFund: response.data.newBalance } : null);
        
        // Clear form
        setAmount('');
        setRemark('');
        
        // Refresh history
        fetchHistory(tableSearch);
      } else {
        showSnackbar(response.data?.message || 'Failed to update Welfare Fund', 'error');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Error updating Welfare Fund';
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Export CSV (permission gated)
  const handleExportCSV = () => {
    if (!can('smartPoint.welfare.export')) return;
    if (history.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    const headers = ['SL No', 'Transaction Date', 'Customer ID', 'Customer Name', ...(showSensitive ? ['Mobile', 'VPA ID'] : []), 'Bill Amount', 'Mode of Pay', 'Amount', 'Type', 'Remarks', 'Current Welfare Fund Balance'];
    const rows = history.map(item => [
      item.serialNumber,
      `"${formatDateTimeIST(item.transactionDate)}"`,
      `"${customerNoOf(item)}"`,
      `"${item.customerName}"`,
      ...(showSensitive ? [`"${item.customerMobile}"`, `"${vpaShown(item.vpaId)}"`] : []),
      billShown(item.billAmount),
      modeShown(item),
      formatAmount4Decimals(item.amount),
      item.type,
      `"${(item.remarks || '').replace(/"/g, '""')}"`,
      formatAmount4Decimals(item.currentBalance)
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Customer_Welfare_Fund_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon fontSize="large" color="success" />
            CUSTOMER WELFARE MANAGE
          </Typography>

          {/* CSV Export Button (permission gated) */}
          {can('smartPoint.welfare.export') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 'bold', borderRadius: 2 }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* 1. Add / Deduct Welfare Fund Card */}
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
            Add / Deduct Customer Welfare Fund
          </Typography>

          <Grid container spacing={3}>
            {/* Field 1: Search Customer */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Search Customer (Mobile / VPA ID / Customer No):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Enter Mobile Number / VPA ID / Customer No"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchCustomer()}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearchCustomer}
                  disabled={customerSearchLoading}
                  startIcon={customerSearchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                >
                  Search
                </Button>
              </Box>
            </Grid>

            {/* Field 2: Show Customer Details & Available Welfare Fund */}
            <Grid item xs={12} md={6}>
              {searchedCustomer ? (
                <Card variant="outlined" sx={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', borderRadius: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#166534' }}>
                      Customer Found: {searchedCustomer.customerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {showSensitive ? `Mobile: ${searchedCustomer.customerMobile} | ` : ''}Customer ID: {customerNoOf(searchedCustomer)}
                    </Typography>
                    {showSensitive && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#15803d', mt: 0.5 }}>
                      VPA / UPI ID: <span style={{ fontFamily: 'monospace', color: '#166534' }}>{searchedCustomer.vpaId || 'N/A'}</span>
                    </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Show available Welfare Fund:
                      </Typography>
                      <Chip
                        label={`₹ ${formatAmount4Decimals(searchedCustomer.availableWelfareFund)}`}
                        color="success"
                        sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ p: 2, border: '1px dashed #cbd5e1', borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Search customer above to view available Welfare Fund & VPA ID
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Field 3: Enter Amount */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Enter Amount (₹)"
                placeholder="e.g. 1000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Grid>

            {/* Field 4: Choose Type (Credit / Debit) */}
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                  Choose Type:
                </FormLabel>
                <RadioGroup
                  row
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <FormControlLabel value="Credit" control={<Radio color="success" />} label="Credit (+)" />
                  <FormControlLabel value="Debit" control={<Radio color="error" />} label="Debit (-)" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Field 5: Remark */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Remark"
                placeholder="e.g. Monthly Welfare grant / Correction"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </Grid>

            {/* Submit Button */}
            {can('smartPoint.welfare.adjust') && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color={type === 'Credit' ? 'success' : 'error'}
                  size="large"
                  startIcon={type === 'Credit' ? <AddCircleOutlineIcon /> : <RemoveCircleOutlineIcon />}
                  onClick={handleFormSubmit}
                  disabled={loading || !searchedCustomer}
                  fullWidth
                  sx={{ py: 1.5, fontWeight: 'bold', fontSize: '1rem' }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : `Submit ${type} Welfare Fund`}
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* 2. Show History Table */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Welfare Fund Transaction History
            </Typography>

            {/* Search Filter for Table */}
            <TextField
              size="small"
              placeholder="Filter by Mobile / Name / Customer No / VPA ID / Remarks"
              value={tableSearch}
              onChange={(e) => {
                setTableSearch(e.target.value);
                fetchHistory(e.target.value);
              }}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
              sx={{ width: 350 }}
            />
          </Box>

          <TableContainer sx={{ minHeight: 450, maxHeight: 'calc(100vh - 260px)', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>SL No</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Transaction Date</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Customer Details</TableCell>
                  {showSensitive && <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>VPA / UPI ID</TableCell>}
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Bill Amount</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Mode of Pay</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Remarks</TableCell>
                  <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>Current Welfare Fund balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={showSensitive ? 10 : 9} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showSensitive ? 10 : 9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No Welfare Fund transactions found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((row, index) => (
                    <TableRow key={row._id || index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {formatDateTimeIST(row.transactionDate)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {row.customerName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {showSensitive ? `Mob: ${row.customerMobile} | ` : ''}Customer ID: {customerNoOf(row)}
                        </Typography>
                      </TableCell>
                      {showSensitive && (
                      <TableCell>
                        <Chip
                          label={vpaShown(row.vpaId)}
                          size="small"
                          variant="outlined"
                          color="success"
                          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {row.billAmount === null || row.billAmount === undefined ? '-' : `₹ ${billShown(row.billAmount)}`}
                      </TableCell>
                      <TableCell>{modeShown(row)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        ₹ {formatAmount4Decimals(row.amount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.type}
                          color={row.type === 'Credit' ? 'success' : 'error'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>{row.remarks || 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        ₹ {formatAmount4Decimals(row.currentBalance)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Confirmation Modal */}
        <Dialog open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: type === 'Credit' ? 'success.main' : 'error.main', color: 'white', fontWeight: 'bold' }}>
            Confirm Welfare Fund {type}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="body1">
                <strong>Customer:</strong> {searchedCustomer?.customerName}{showSensitive ? ` (${searchedCustomer?.customerMobile})` : ''}
              </Typography>
              {showSensitive && (
              <Typography variant="body1">
                <strong>VPA / UPI ID:</strong> <span style={{ fontFamily: 'monospace', color: '#15803d', fontWeight: 'bold' }}>{searchedCustomer?.vpaId || 'N/A'}</span>
              </Typography>
              )}
              <Typography variant="body1">
                <strong>Available Welfare Fund:</strong> ₹ {formatAmount4Decimals(searchedCustomer?.availableWelfareFund)}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                <strong>Type:</strong> <Chip label={type} color={type === 'Credit' ? 'success' : 'error'} size="small" />
              </Typography>
              <Typography variant="body1">
                <strong>Amount to {type}:</strong> ₹ {formatAmount4Decimals(amount)}
              </Typography>
              <Typography variant="body1">
                <strong>New Balance will be:</strong> ₹{' '}
                {formatAmount4Decimals(
                  type === 'Credit'
                    ? (searchedCustomer?.availableWelfareFund || 0) + parseFloat(amount || 0)
                    : (searchedCustomer?.availableWelfareFund || 0) - parseFloat(amount || 0)
                )}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTransaction}
              variant="contained"
              color={type === 'Credit' ? 'success' : 'error'}
            >
              Confirm & Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CustomerWelfareManage;
